import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { PAGE_LAYOUT } from "../../layout/pageLayout";
import {
  ITEM_STATUS,
  ITEM_STATUS_LABELS,
  ITEM_STATUS_OPTIONS,
  READINESS_STATUS,
  SECTIONS,
  ITEMS,
} from "../../googleVisibility/catalog";
import {
  assessVisibility,
  displayedNextActions,
  getItemNote,
  getItemStatus,
} from "../../googleVisibility/scoring";
import {
  applyItemState,
  createEmptyItemStates,
  overlayDirtyItemStates,
  sanitizeItemNote,
} from "../../googleVisibility/itemState";
import {
  loadGoogleVisibilityStates,
  saveGoogleVisibilityItemState,
} from "../../googleVisibility/persistence";
import { actorCanAccessGoogleVisibility } from "../../helpers/googleVisibilityAccess";
import "./GoogleVisibilityScreen.css";

const GATE_STRIP_LABELS = Object.freeze({
  gbp_configured: "GBP",
  search_console_verified: "Search Console",
  sitemap_submitted: "Sitemap",
  indexing_resolved: "Indexing",
  core_website_seo: "Website SEO",
  citations_consistent: "Citations",
});

function statusClass(status) {
  return String(status || "")
    .toLowerCase()
    .replace(/\s+/g, "_");
}

function gateLabel(status) {
  if (status === "passed") return "Passed";
  if (status === "blocked") return "Blocked";
  if (status === "in_progress") return "In Progress";
  return "Open";
}

function ChecklistItem({
  item,
  status,
  note,
  disabled,
  pending,
  onStatusChange,
  onNoteBlur,
  idPrefix = "gvis-item",
  compact = false,
}) {
  const [draftNote, setDraftNote] = useState(note);
  const completeLocked = status === ITEM_STATUS.COMPLETE;

  useEffect(() => {
    setDraftNote(note);
  }, [note]);

  return (
    <article
      className={`gvis-item${compact ? " gvis-item--compact" : ""}`}
      id={`${idPrefix}-${item.id}`}
      data-testid={`${idPrefix}-${item.id}`}
    >
      <div className="gvis-item-top">
        <div>
          <div className="gvis-item-heading">
            <h3 className="gvis-item-title">{item.title}</h3>
            <span
              className={`gvis-pill gvis-pill--${statusClass(status)}`}
              data-testid={`${idPrefix}-${item.id}-status`}
            >
              {ITEM_STATUS_LABELS[status] || ITEM_STATUS_LABELS[ITEM_STATUS.NOT_STARTED]}
            </span>
          </div>
          {compact ? (
            item.recommendedAction ? (
              <p className="gvis-item-summary">{item.recommendedAction}</p>
            ) : null
          ) : (
            <p className="gvis-item-summary">{item.summary}</p>
          )}
        </div>
        <div className="gvis-status-group" role="group" aria-label={`${item.title} status`}>
          {ITEM_STATUS_OPTIONS.map((option) => {
            const lockCompleteAction =
              option.value === ITEM_STATUS.COMPLETE && completeLocked;
            return (
              <button
                key={option.value}
                type="button"
                className={`gvis-status-btn${status === option.value ? " is-active" : ""}`}
                data-status={option.value}
                aria-pressed={status === option.value}
                disabled={disabled || pending || lockCompleteAction}
                onClick={() => onStatusChange(item.id, option.value, draftNote)}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
      <input
        className="gvis-note"
        value={draftNote}
        disabled={disabled || pending}
        maxLength={280}
        placeholder="Note (optional)"
        aria-label={`${item.title} note`}
        onChange={(event) => setDraftNote(event.target.value)}
        onBlur={() => {
          if (sanitizeItemNote(draftNote) === sanitizeItemNote(note)) {
            return;
          }
          onNoteBlur(item.id, draftNote);
        }}
      />
    </article>
  );
}

export function GoogleVisibilityScreen({ actor, onBack, client }) {
  const [states, setStates] = useState(createEmptyItemStates);
  const [persistAvailable, setPersistAvailable] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSectionId, setOpenSectionId] = useState(null);
  const [showScoringHelp, setShowScoringHelp] = useState(false);
  const [pendingItemIds, setPendingItemIds] = useState(() => new Set());

  const statesRef = useRef(states);
  const dirtyItemIdsRef = useRef(new Set());
  const writeGenerationRef = useRef(new Map());
  const actionSlotIdsRef = useRef(null);
  const mountedRef = useRef(true);
  statesRef.current = states;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const allowed = actorCanAccessGoogleVisibility(actor);
  const assessment = useMemo(() => assessVisibility(states), [states]);

  if (!loading && allowed && !actionSlotIdsRef.current) {
    actionSlotIdsRef.current = assessment.actions.map((item) => item.id);
  }

  const nextActionItems = displayedNextActions(states, actionSlotIdsRef.current);

  useEffect(() => {
    let cancelled = false;
    if (!allowed) {
      setLoading(false);
      return undefined;
    }

    dirtyItemIdsRef.current = new Set();
    actionSlotIdsRef.current = null;
    setPendingItemIds(new Set());
    setLoading(true);

    void (async () => {
      const result = await loadGoogleVisibilityStates(actor, client);
      if (cancelled) {
        return;
      }
      const merged = overlayDirtyItemStates(
        result.states,
        statesRef.current,
        dirtyItemIdsRef.current,
      );
      statesRef.current = merged;
      setStates(merged);
      setPersistAvailable(result.persistAvailable);
      setStatusMessage(result.persistAvailable ? null : result.error);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [actor, allowed, client]);

  const persistItem = useCallback(
    async (itemId, status, note) => {
      const result = applyItemState(statesRef.current, itemId, { status, note });
      if (!result.changed) {
        return;
      }

      statesRef.current = result.states;
      dirtyItemIdsRef.current.add(itemId);
      setStates(result.states);

      if (!persistAvailable) {
        return;
      }

      const generation = (writeGenerationRef.current.get(itemId) || 0) + 1;
      writeGenerationRef.current.set(itemId, generation);
      setPendingItemIds((prev) => {
        const next = new Set(prev);
        next.add(itemId);
        return next;
      });

      const applied = result.states[itemId];
      const { data, error } = await saveGoogleVisibilityItemState(
        actor,
        { itemId, status: applied.status, note: applied.note },
        client,
      );

      if (writeGenerationRef.current.get(itemId) !== generation || !mountedRef.current) {
        return;
      }

      setPendingItemIds((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
      setStatusMessage(error?.message || null);

      if (!error && data?.item_key) {
        const synced = applyItemState(statesRef.current, data.item_key, {
          status: data.status,
          note: data.note,
        });
        const nextStates = {
          ...synced.states,
          [data.item_key]: {
            ...(synced.states[data.item_key] || {}),
            evidence:
              data.evidence && typeof data.evidence === "object" ? data.evidence : {},
            updatedAt: data.updated_at || null,
            updatedByProfileId: data.updated_by_profile_id || null,
          },
        };
        statesRef.current = nextStates;
        setStates(nextStates);
      }
    },
    [actor, client, persistAvailable],
  );

  const persistNote = useCallback(
    (itemId, note) => {
      const status = getItemStatus(statesRef.current, itemId);
      return persistItem(itemId, status, note);
    },
    [persistItem],
  );

  if (!allowed) {
    return null;
  }

  const readinessTone =
    assessment.readinessStatus === READINESS_STATUS.GATES_CLEAR
      ? "gates_clear"
      : "not_ready";

  return (
    <CrewPilotModuleShell
      title="Google Visibility"
      subtitle="Surface Renaud Inc · surfacerenaud.ca"
      onBack={onBack}
      layout={PAGE_LAYOUT.COMMAND}
      primary
    >
      <div
        className="gvis-screen"
        data-testid="google-visibility-screen"
        aria-busy={loading}
      >
        {statusMessage ? (
          <p className="gvis-banner gvis-banner--error" role="alert" data-testid="gvis-status-message">
            {statusMessage}
          </p>
        ) : null}

        <div className="gvis-status-row">
          <section className="gvis-card" data-testid="gvis-readiness">
            <div className="gvis-card-head">
              <p className="gvis-kicker">Readiness status</p>
              <button
                type="button"
                className="gvis-info-btn"
                aria-expanded={showScoringHelp}
                aria-controls="gvis-scoring-help"
                data-testid="gvis-scoring-info"
                onClick={() => setShowScoringHelp((open) => !open)}
              >
                i
                <span className="gvis-visually-hidden">Progress versus readiness</span>
              </button>
            </div>
            <div className={`gvis-readiness gvis-readiness--${readinessTone}`}>
              {assessment.readinessStatus}
            </div>
            <p className="gvis-muted">
              {assessment.blockingGates.length
                ? `${assessment.blockingGates.length} of 6 gates open`
                : "All 6 gates passed"}
            </p>
            {showScoringHelp ? (
              <p id="gvis-scoring-help" className="gvis-info-pop" data-testid="gvis-progress-vs-readiness">
                Progress is checklist completion. Readiness is the six gates only.
              </p>
            ) : null}
            <ul className="gvis-gate-strip" data-testid="gvis-gates">
              {assessment.gates.map((gate) => (
                <li
                  key={gate.id}
                  className={`gvis-gate-chip gvis-gate-chip--${statusClass(gate.status)}`}
                  data-testid={`gvis-gate-${gate.id}`}
                  title={`${gate.title}: ${gateLabel(gate.status)}`}
                >
                  <span className="gvis-gate-chip-label">
                    {GATE_STRIP_LABELS[gate.id] || gate.title}
                  </span>
                  <span className="gvis-gate-chip-state">{gateLabel(gate.status)}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="gvis-card" data-testid="gvis-progress">
            <div className="gvis-progress-top">
              <p className="gvis-kicker">Overall progress</p>
              <span className="gvis-progress-value">{assessment.percent}%</span>
            </div>
            <div
              className="gvis-bar"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={assessment.percent}
            >
              <span style={{ width: `${assessment.percent}%` }} />
            </div>
            <div className="gvis-counts">
              <span>
                <strong>
                  {assessment.counts.complete}/{assessment.counts.total}
                </strong>{" "}
                complete
              </span>
              <span>
                <strong>{assessment.counts.blocked}</strong> blocked
              </span>
            </div>
          </section>
        </div>

        <section className="gvis-card gvis-blocked" data-testid="gvis-blocked">
          <p className="gvis-kicker">Critical blockers</p>
          {assessment.blocked.length ? (
            assessment.blocked.map((item) => (
              <ChecklistItem
                key={item.id}
                item={item}
                status={getItemStatus(states, item.id)}
                note={getItemNote(states, item.id)}
                disabled={!persistAvailable}
                pending={pendingItemIds.has(item.id)}
                onStatusChange={persistItem}
                onNoteBlur={persistNote}
                idPrefix="gvis-blocked"
                compact
              />
            ))
          ) : (
            <p className="gvis-muted">None</p>
          )}
        </section>

        <section className="gvis-card gvis-next-card" data-testid="gvis-next">
          <p className="gvis-kicker">Next actions</p>
          {nextActionItems.length ? (
            nextActionItems.map((item) => (
              <ChecklistItem
                key={item.id}
                item={item}
                status={getItemStatus(states, item.id)}
                note={getItemNote(states, item.id)}
                disabled={!persistAvailable}
                pending={pendingItemIds.has(item.id)}
                onStatusChange={persistItem}
                onNoteBlur={persistNote}
                idPrefix="gvis-next"
                compact
              />
            ))
          ) : (
            <p className="gvis-muted">No open items. Review blocked work or remaining gates.</p>
          )}
        </section>

        <div className="gvis-sections">
          {SECTIONS.map((section) => {
            const summary = assessment.sections.find((row) => row.id === section.id);
            const sectionItems = ITEMS.filter((item) => item.sectionId === section.id);
            const open = openSectionId === section.id;
            return (
              <section
                key={section.id}
                className="gvis-card"
                data-testid={`gvis-section-${section.id}`}
              >
                <button
                  type="button"
                  className="gvis-section-toggle"
                  aria-expanded={open}
                  onClick={() => setOpenSectionId(open ? null : section.id)}
                >
                  <span>
                    <span className="gvis-section-title">{section.title}</span>
                    <span className="gvis-muted">
                      {summary?.complete || 0}/{summary?.total || sectionItems.length} ·{" "}
                      {summary?.percent || 0}%
                    </span>
                  </span>
                  <span className="gvis-section-caret">{open ? "Hide" : "Show"}</span>
                </button>
                {open
                  ? sectionItems.map((item) => (
                      <ChecklistItem
                        key={item.id}
                        item={item}
                        status={getItemStatus(states, item.id)}
                        note={getItemNote(states, item.id)}
                        disabled={!persistAvailable}
                        pending={pendingItemIds.has(item.id)}
                        onStatusChange={persistItem}
                        onNoteBlur={persistNote}
                      />
                    ))
                  : null}
              </section>
            );
          })}
        </div>
      </div>
    </CrewPilotModuleShell>
  );
}
